import { css } from 'aphrodite';
import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { THEME } from '../../../../../components-v2/Theme/theme';
import { mainChartColor } from '../../../helpers/chartColors';
import { formatCurrency } from '../../../helpers/formatCurrency';
import { formatDealSizeRangeLabel } from '../../helpers/formatDealSizeAxisTick';
import type { DealSizeBucketRow } from '../../helpers/opportunityReportingChartTypes';
import { styleSheet } from './styles';

function DealSizeDistributionTooltip({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ payload: DealSizeBucketRow & { color: string } }>;
}) {
	if (!active || !payload?.length) {
		return null;
	}
	const row = payload[0]?.payload;
	if (!row) {
		return null;
	}
	return (
		<div className={css(styleSheet.tooltip)}>
			<p className={css(styleSheet.tooltipLabel)}>{row.range}</p>
			<p className={css(styleSheet.tooltipValue)}>Count: {row.count}</p>
			<p className={css(styleSheet.tooltipValue)}>Total deal size: {formatCurrency(row.dealSize)}</p>
		</div>
	);
}

interface DealSizeDistributionChartProps {
	data: DealSizeBucketRow[];
	isLoading?: boolean;
	className?: string;
}

export const DealSizeDistributionChart: React.FC<DealSizeDistributionChartProps> = ({ data, isLoading, className }) => {
	const chartData = useMemo(() => {
		const withColor = data.map(item => ({ ...item, color: mainChartColor() }));
		if (!withColor.some(item => item.count > 0)) {
			return [];
		}
		return withColor;
	}, [data]);

	if (isLoading) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.loading)}>Loading chart data...</div>
			</div>
		);
	}

	if (chartData.length === 0) {
		return (
			<div className={`${css(styleSheet.container)} ${className || ''}`}>
				<div className={css(styleSheet.empty)}>No deal size data in the loaded opportunities</div>
			</div>
		);
	}

	return (
		<div className={`${css(styleSheet.container)} ${className || ''}`}>
			<h2 className={css(styleSheet.title)}>Deal size distribution</h2>
			<ResponsiveContainer width='100%' height={320}>
				<BarChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
					<CartesianGrid strokeDasharray='3 3' stroke={THEME.colors.border.main} />
					<XAxis
						dataKey='range'
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 11, fill: THEME.colors.text.main }}
						tickFormatter={formatDealSizeRangeLabel}
					/>
					<YAxis
						allowDecimals={false}
						stroke={THEME.colors.text.main}
						tick={{ fontSize: 11, fill: THEME.colors.text.main }}
					/>
					<Tooltip content={<DealSizeDistributionTooltip />} />
					<Bar dataKey='count' radius={[4, 4, 0, 0]}>
						{chartData.map((item, index) => (
							<Cell key={`${item.range}-${index}`} fill={item.color} />
						))}
					</Bar>
				</BarChart>
			</ResponsiveContainer>
		</div>
	);
};
